<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canViewLoans,
  type Loan,
  LoanCreateForm,
  LoanList,
  LoanSummaryCards,
  LoanTermsDialog,
  type UpdateLoanTermsInput,
  useLoans,
  useLoanSummary,
  useLoanTerms,
} from '@/features/loans'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewLoans(session.can))
const ledgerId = () => session.activeLedger.value?.id

const loans = useLoans({
  ledgerId,
  enabled: () => canView.value,
})

const portfolio = useLoanSummary({
  ledgerId,
  enabled: () => canView.value,
})

const terms = useLoanTerms({ ledgerId })
const editing = ref<Loan | undefined>(undefined)

const createErrorMessage = computed(() =>
  loans.hasCreateError.value ? (loans.createErrorDetail.value ?? t('loans.createErrorBody')) : undefined,
)

const termsErrorMessage = computed(() =>
  terms.hasError.value ? (terms.errorDetail.value ?? t('loans.termsErrorBody')) : undefined,
)

function createLoan(input: Parameters<typeof loans.create>[0]): void {
  loans.create(input).catch(() => {
    // surfaced through loans.createError; nothing further to do here
  })
}

function saveTerms(input: UpdateLoanTermsInput): void {
  const loan = editing.value
  if (loan === undefined) return
  terms
    .update(loan.id, input)
    .then(() => {
      editing.value = undefined
    })
    .catch(() => {
      // surfaced through terms.hasError
    })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('loans.title')">
      <template #subtitle>{{ t('loans.subtitle') }}</template>
      <template v-if="canView" #actions>
        <LoanCreateForm
          :is-submitting="loans.isCreating.value"
          :error-message="createErrorMessage"
          @create="createLoan"
        />
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('loans.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('loans.noAccess') }}
    </p>

    <template v-else>
      <LoanSummaryCards v-if="portfolio.portfolio.value" :summary="portfolio.portfolio.value" />

      <LoanList
        :loans="loans.loans.value"
        :is-loading="loans.isLoading.value"
        :error="loans.error.value"
        :has-history="loans.hasHistory"
        :can-edit="canView"
        :retrying="loans.retrying.value"
        @retry="loans.retry()"
        @edit="editing = $event"
      />
    </template>

    <LoanTermsDialog
      v-if="editing"
      :key="editing.id"
      :loan="editing"
      :is-submitting="terms.isSaving.value"
      :error-message="termsErrorMessage"
      @save="saveTerms"
      @close="editing = undefined"
    />
  </PageShell>
</template>
